import React, { useState, useEffect, useRef } from 'react';
import Map, { MapRef } from '../mapbox/map';
import IncidentList from '../IncidentList/IncidentList';
import ProximityLegend from '../ProximityLegend/ProximityLegend';
import Sidebar from '../Sidebar/Sidebar';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import HaloSearch from '../Header/HaloSearch';
import HaloService from '../../services/haloService';
import { Incident, fetchIncidents } from '../../services/supabase';
import { cameras } from '../../data/cameras';
import { isWithinRangeOfCamera } from '../../utils/geoUtils';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const mapRef = useRef<MapRef>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(true);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [filterType, setFilterType] = useState<'single' | 'range'>('single');
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [dateRange, setDateRange] = useState<[Date, Date]>([today, today]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(['All Categories']);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>(['All Priorities']);
  const [showCameras, setShowCameras] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [withinCameraRange, setWithinCameraRange] = useState(false);
  const [haloMatches, setHaloMatches] = useState<any[]>([]);
  const [isHaloLoading, setIsHaloLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Initialize haloService
  const haloService = new HaloService();

  const handleCameraLocation = (lat: number, lon: number) => {
    if (mapRef.current) {
      mapRef.current.flyToCamera(lat, lon);
    }
  };

  // Load incidents with current date initially
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        // Fetch incidents for today
        const { incidents: fetchedIncidents } = await fetchIncidents({
          singleDate: today,
          categories: selectedCategories,
          priorities: selectedPriorities
        });

        setIncidents(filterByRange(fetchedIncidents));
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Add loading screen effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoadingScreen(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Helper function to filter incidents by camera range
  const filterByRange = (incidents: Incident[]) => {
    if (!withinCameraRange) return incidents;

    return incidents.filter(incident => {
      const point = incident.intersection_point;
      if (!point) return false;

      let lat: number | undefined;
      let lng: number | undefined;

      // Handle different coordinate formats
      if ('coordinates' in point && Array.isArray(point.coordinates)) {
        [lng, lat] = point.coordinates;
      } else if ('latitude' in point && 'longitude' in point) {
        lat = point.latitude;
        lng = point.longitude;
      }

      if (typeof lat !== 'number' || typeof lng !== 'number') return false;

      return isWithinRangeOfCamera(lat, lng, cameras, 1000); // 1000 meters = 1km range
    });
  };

  // Update incidents when filters change (manual control only; no auto-refresh)
  useEffect(() => {
    let isCancelled = false;

    const updateIncidents = async () => {
      setLoading(true);
      try {
        const { incidents: fetchedIncidents } = await fetchIncidents({
          singleDate: filterType === 'single' ? selectedDate : undefined,
          startDate: filterType === 'range' ? dateRange[0] : undefined,
          endDate: filterType === 'range' ? dateRange[1] : undefined,
          categories: selectedCategories,
          priorities: selectedPriorities
        });

        const allIncidents = [...filterByRange(fetchedIncidents)];
        if (!isCancelled) setIncidents(allIncidents);
      } catch (error) {
        if (!isCancelled) console.error("Error updating incidents:", error);
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    updateIncidents();
    return () => { isCancelled = true; };
  }, [filterType, selectedDate, dateRange, selectedCategories, selectedPriorities, withinCameraRange]);

  const handleFocusIncident = (incidentId: string) => {
    const incident = incidents.find(inc => inc.id === incidentId);
    if (!incident || !mapRef.current) return;

    const point = incident.intersection_point;
    if (!point) return;

    let coordinates: [number, number] | undefined;

    // Handle different coordinate formats
    if ('coordinates' in point && Array.isArray(point.coordinates)) {
      coordinates = point.coordinates as [number, number];
    } else if ('latitude' in point && 'longitude' in point) {
      coordinates = [point.longitude, point.latitude];
    }

    if (!coordinates) return;

    mapRef.current.flyToIncident(coordinates, incident);
  };

  const handleDateRangeChange = (range: Date | [Date, Date]) => {
    if (Array.isArray(range)) {
      setDateRange(range);
    } else {
      setSelectedDate(range);
    }
  };

  const handleHaloSearch = async (description: string, searchType: string = 'person') => {
    setIsHaloLoading(true);
    try {
      const response = await haloService.runScan(description, 'All Regions', searchType);
      setHaloMatches(response.matches);
    } catch (error) {
      console.error('Error running Halo scan:', error);
    } finally {
      setIsHaloLoading(false);
    }
  };

  return (
    <>
      {showLoadingScreen && <LoadingScreen />}
      <div className="dashboard">
        <div className="title-bar">
          <div className="left-section">
            <HaloSearch onSearch={handleHaloSearch} />
          </div>
          <div className="center-section">
            <div className="title-main">Archangel AI</div>
            <div className="title-sub">Beta Mode</div>
          </div>
          <div className="right-section">
            <div className="platform-controls">
              <div className="platform-time">{currentTime.toLocaleTimeString()}</div>
              <div className="platform-stats">
                <span>TRACKING {incidents.length} INCIDENTS</span>
                {showCameras && (
                  <>
                    <span className="stat-divider">|</span>
                    <span>53 CAMERAS ACTIVE</span>
                  </>
                )}
                {isHaloLoading && (
                  <>
                    <span className="stat-divider">|</span>
                    <span className="halo-status">HALO SCANNING...</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="dashboard-content">
          <Sidebar
            dateRange={dateRange}
            selectedDate={selectedDate}
            filterType={filterType}
            onFilterTypeChange={setFilterType}
            onDateRangeChange={handleDateRangeChange}
            selectedCategories={selectedCategories}
            onCategoryChange={setSelectedCategories}
            categories={['All Categories', 'Traffic', 'Crime', 'Fire']}
            selectedPriorities={selectedPriorities}
            onPriorityChange={setSelectedPriorities}
            priorities={['All Priorities', 'A', 'B', 'C']}
            showCameras={showCameras}
            onShowCamerasChange={setShowCameras}
            showHeatmap={showHeatmap}
            onShowHeatmapChange={setShowHeatmap}
            withinCameraRange={withinCameraRange}
            onWithinRangeChange={setWithinCameraRange}
          />
          <div className="map-section">
            <Map ref={mapRef} incidents={incidents} showHeatmap={showHeatmap} />
            <ProximityLegend />
          </div>
          <div className="incident-section">
            <IncidentList
              incidents={incidents}
              onFocusIncident={handleFocusIncident}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard; 